import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import {
  getProfileSection,
  isSectionDisabled,
  PROFILE_SECTIONS,
  type ProfileSectionValue,
} from "@/components/profile/profile-sections";

type ProfileSectionNavProps = {
  value: string;
  onValueChange: (value: string) => void;
  legalApprovalNeeded?: boolean;
};

export function ProfileSectionNav({
  value,
  onValueChange,
  legalApprovalNeeded = false,
}: ProfileSectionNavProps) {
  const isMobile = useIsMobile();
  const activeSection = getProfileSection(value);

  if (isMobile) {
    const ActiveIcon = activeSection?.icon;

    return (
      <div className="sticky top-0 z-20 mb-4 bg-dark-bg/95 py-1.5 backdrop-blur-sm">
        <Select value={value} onValueChange={onValueChange}>
          <SelectTrigger
            aria-label="Profile section"
            className="h-10 w-full rounded-lg border-white/15 bg-dark-slate text-sm text-white focus:ring-primary/40"
          >
            <SelectValue placeholder="Select section">
              {activeSection && (
                <span className="flex items-center gap-2">
                  {ActiveIcon && <ActiveIcon className="h-3.5 w-3.5 shrink-0 text-primary" />}
                  <span>{activeSection.label}</span>
                </span>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="border-primary/20 bg-dark-card text-white">
            {PROFILE_SECTIONS.map((section) => {
              const Icon = section.icon;
              const disabled = isSectionDisabled(section.value, legalApprovalNeeded);

              return (
                <SelectItem
                  key={section.value}
                  value={section.value}
                  disabled={disabled}
                  className="focus:bg-primary/20 focus:text-white"
                >
                  <span className="flex items-center gap-2">
                    <Icon className="h-4 w-4 shrink-0" />
                    {section.label}
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <TabsList className="mb-6 w-full flex flex-nowrap gap-1 overflow-x-auto border border-primary/20 bg-dark-slate p-1 snap-x snap-mandatory">
      {PROFILE_SECTIONS.map((section) => {
        const Icon = section.icon;
        const disabled = isSectionDisabled(section.value, legalApprovalNeeded);

        return (
          <TabsTrigger
            key={section.value}
            value={section.value}
            disabled={disabled}
            className={cn(
              "flex items-center gap-1 text-xs sm:gap-2 sm:text-sm text-white",
              section.highlight
                ? "border border-primary/30 bg-primary/10 text-primary hover:bg-primary hover:text-black data-[state=active]:bg-primary data-[state=active]:text-black data-[state=active]:shadow-neon-glow"
                : undefined
            )}
          >
            <Icon className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>{section.label}</span>
          </TabsTrigger>
        );
      })}
    </TabsList>
  );
}

export type { ProfileSectionValue };
